"use client";

import { useEffect, useRef, useState } from "react";

import { useFocusWhenInView } from "../hooks/use-focus-when-in-view";
import { cn } from "../utils";
import { WidgetSubmitButton } from "./widget-submit-button";

export type InlineInputProps = {
  placeholder?: string;
  submitLabel?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Focus only once the input is visible in the viewport and scrolling has settled. */
  autoFocusInView?: boolean;
  onSubmit?: (value: string) => void;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
};

export function InlineInput({
  placeholder = "Type your answer...",
  submitLabel = "Send",
  disabled = false,
  autoFocus = true,
  autoFocusInView = false,
  onSubmit,
  value: controlledValue,
  onChange,
  className,
}: InlineInputProps) {
  const [internalValue, setInternalValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  useEffect(() => {
    if (autoFocusInView || disabled) return;
    // `preventScroll: true` — scroll-snap (chatless) owns positioning; focus
    // must land on the input without nudging the feed's snap point.
    if (autoFocus) inputRef.current?.focus({ preventScroll: true });
  }, [autoFocus, autoFocusInView, disabled]);

  useFocusWhenInView(inputRef, autoFocusInView && !disabled);

  const updateValue = (next: string) => {
    if (isControlled) {
      onChange?.(next);
    } else {
      setInternalValue(next);
    }
  };

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onSubmit?.(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const showSubmit = !!value.trim() && !disabled;

  return (
    <div className={cn("flex flex-col gap-3 w-full sm:max-w-md", className)}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => updateValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-full border-0 border-b border-border bg-transparent pb-2 text-base outline-none placeholder:text-text-light focus:border-accent disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={disabled}
      />
      {showSubmit && (
        <WidgetSubmitButton
          $variant="secondary"
          $size="sm"
          type="button"
          onClick={handleSubmit}
          className="animate-fade-in self-start"
        >
          {submitLabel}
        </WidgetSubmitButton>
      )}
    </div>
  );
}
